import { Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useAuth } from '@/contexts/AuthContext';

/** True only when the account really has access. */
export const useSubscriptionLock = () => {
  const { subscriptionStatus } = useAuth();
  return { unlocked: subscriptionStatus?.has_access === true, subscriptionStatus };
};

/**
 * Reusable paywall slide-up: shows a blurred, dead preview of the feature with a
 * subscribe prompt over it. Pass the feature name and the content to blur.
 */
export const LockedFeatureSheet = ({ feature, open, onOpenChange, children, testId }) => {
  const navigate = useNavigate();
  const slug = (feature || 'feature').toLowerCase().replace(/[^a-z0-9]+/g, '-');

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom"
        className="bg-[#07090C] border-t border-white/10 rounded-t-3xl max-h-[88vh] overflow-hidden pb-[calc(6.5rem+env(safe-area-inset-bottom,0px))]"
        data-testid={testId || `locked-sheet-${slug}`}>
        <SheetHeader className="text-left">
          <SheetTitle className="font-heading font-black uppercase tracking-tight text-white flex items-center gap-2">
            <Lock className="w-4 h-4 text-white/50" />
            {feature}
          </SheetTitle>
        </SheetHeader>

        <div className="relative mt-3">
          <div className="blur-sm pointer-events-none select-none" aria-hidden="true" data-testid={`locked-preview-${slug}`}>
            {children}
          </div>
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 px-6 text-center"
               style={{ background: 'linear-gradient(180deg, rgba(7,9,12,0.55) 0%, rgba(7,9,12,0.88) 100%)' }}
               data-testid={`locked-overlay-${slug}`}>
            <p className="font-heading font-black text-white text-lg leading-snug" data-testid="locked-message">
              You must be subscribed to use this feature
            </p>
            <button onClick={() => navigate('/subscribe')}
              className="px-6 py-3 rounded-xl font-heading font-bold text-sm text-white active:scale-[0.97] transition-transform"
              style={{ background: '#0A84FF', boxShadow: '0 8px 24px rgba(10,132,255,0.35)' }}
              data-testid="locked-subscribe-btn">
              Subscribe to unlock
            </button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default LockedFeatureSheet;
